<template lang="pug">
  div(v-if="applications.length > 0")
    template(v-for="a in pageItems")
      ApplicationListTile(
        :id="a.application_id"
        :ctas="ctas(a)"
        :topRight="displayDate(a)"
        :icon="( companyLogo(getLender(a)) ) "
        :topLeftSubtext="getSubtext(a)"
        :loading="applicationsLoading[a.application_id]"
        :latestActivity="latestActivity(a)"
        :details="getDetails(a)"
        :authorized="authorized(a)"
        :lender="getLender(a)"
      )
    .text-xs-center.py-3
      v-pagination(
        v-if="pages > 1"
        :length="pages"
        v-model="page"
        @input="paginateHistory($event)"
      )

    //Greensky popup
    v-dialog(v-model="pdfDialog" max-width="400px")
      v-card
        .pt-5
          v-img(
            :src="greensky.logoSvg"
            height=50 contain
          )
          v-card-text(class="pt-3")
            p You can use customer Application Id 
             strong(v-if="currentApplicationId") {{currentApplicationId}} 
             | and last 4 SSN to retrieve the Loan Agreement Document at the link below.
            p
              a(:href="pdfLink()" target="blank" class="wf-link") Go to customer portal
                v-icon.mb-1.ml-1(small color='blue') mdi-open-in-new
              
          v-card-actions
            v-spacer
            v-btn(flat @click="pdfDialog = false") Close
    AuthorizationLinkDialog(
      :email="email"
      :sentEmailDialog="sentEmailDialog"
      :loading="authorizationLinkLoading"
      :authorizationLinkSent="authorizationLinkSent"
      :authorizationLinkMessaging="authorizationLinkMessaging"
      :resendLink="resendLink"
      @close="close()"
      @resend="resendAuthorizationLink"
    )

  //No applications
  div(v-else)
    div
      img(
        :src="noDataImg"
        height="350"
      )
      p(class="pb-4 display-1") You haven't run any Applications yet
    span(class="title")
      p(class="mb-4 grey--text text--darken-1")
        | You can start a 
        router-link(tag="a" to="/credit-application/plans" class="wf-link") New Application 
        | for your customers
</template>

<script>
  import { mapActions } from 'vuex'
  import { title } from 'change-case'
  import ApplicationListTile from 'Components/ApplicationListTile'
  import AuthorizationLinkDialog from "@/components/dialogs/AuthorizationLinkDialog"
  import { greensky, fortiva, ygrene, customfin, stressfree, wellsfargo } from 'Helpers/lenders.js'
  import Loader from 'Components/FanLoader.vue'
  import Lenders from 'Mixins/Lenders'
  import { resultsPerPage } from 'Helpers/constants'
  import { numberWithCommas } from "@/helpers/parsingFunctions"
  import { delayMilliseconds } from 'Helpers/utilFunctions.js'
  import envVars from '@/env_vars'

  export default {
    name: 'applications-list',
    props: {
      applications: {
        type: Array,
        default: () => [],
      },
      searchInput: {
        type: String
      }
    },
    data() {
      return {
        authorizationLinkLoading: true,
        authorizationLinkMessaging: null,
        authorizationLinkSent: null,
        currentApplicationId: null,
        detailKeys: [
          {
            key: 'id',
            short: 'ID',
            long: 'Application ID',
          },
          {
            key: 'account',
            short: 'Account',
            long: 'Account Number',
          },
          {
            key: 'approvedAmount',
            short: 'Approved',
            long: 'Approved Amount',
          },
          {
            key: 'customerTier',
            short: 'Customer',
            long: 'Customer',
          },
          {
            key: 'statusMessage',
            short: 'Message',
            long: 'Status Details',
          }
        ],
        applicationsLoading: {},
        greenskyPlans: [],
        loader: false,
        noDataImg: require('Assets/empty-desert.png'),
        page: 1,
        pageSize: resultsPerPage,
        pdfDialog: false,
        uploadsDialog: false,
        resendLink: null,
        sentEmailDialog: false,
        email: null,
      }
    },
    components: {
      ApplicationListTile,
      AuthorizationLinkDialog,
    },
    mixins: [Lenders],
    watch:{
      searchInput(val){
        if(val){
          this.page = 1
        }
      }
    },
    computed: {
      applicationId() {
        return this.application_id
      },
      pages() {
        return Math.ceil(this.applications.length / resultsPerPage)
      },
      pageItems() {
        return this.applications.slice(
          this.pageSize * (this.page - 1),
          this.pageSize * this.page 
        )
      },
    },
    mounted() {
      //TODO dedupe this from LeadsList
      this.$store.dispatch('getAllGreenskyPlans').then(plans => {
        this.greenskyPlans = plans
      })
      this.checkStatuses()
    },
    methods: {
      ...mapActions([
        'cycleGetStatus',
        'getLoanApplicationHistory',
        'getOffers',
        'setApplicationFromHistory',
        'singleStatusCheck',
        'sendEasyLink',
        'resendEasyLink',
      ]),
      acceptedFortiva(a) {
        return a.lender === 'fortiva' && a.application_status === 'offer_accepted'
      },
      canViewMore (application) {
        //TODO review lender logic
        const isGreensky =  application.lender === 'greensky'
        const isFortiva = application.lender === 'fortiva'
        const isWellsFargo = application.lender === 'wellsfargo'
        const isAvailable = application.application_status === 'offer_available'
        const isAuthorized = application.application_status === 'authorized'
        const isWFError = application.lender === 'wellsfargo' && application.application_status === 'error' 

        return ( isGreensky || isFortiva || isWellsFargo ) && (isAvailable || isAuthorized || isWFError)
      },
      checkStatuses() {
        const { pageItems } = this

        const cfinApps = pageItems.filter(a => a.lender === 'customfin')
        const otherApps = pageItems.filter(a => a.lender != 'customfin')

        if ( (cfinApps.length + otherApps.length) > 0) {

          cfinApps.forEach(a => {
            this.$set(this.applicationsLoading, a.application_id, true)
            this.applicationsLoading[a.application_id] = false
          })

          otherApps.forEach(a => {
            this.$set(this.applicationsLoading, a.application_id, true)

            this.singleStatusCheck( { applicationId:a.application_id, lender:a.lender} ) 
            .finally(() => {
              this.applicationsLoading[a.application_id] = false
            })
          })

          // Promise.all( cFinStatusChecks, otherStatusChecks ).finally(results => {
          //   this.applicationsLoading[this.application_id] = false
          // })
        }

      },
      ctas (a) {
        let list = []
        const id = a.application_id

        //Process Transaction Btn
        if (this.acceptedFortiva(a)) {
          list.push({
            key: 'processTransaction',
            type: 'link',
            label: 'Process Transaction',
            href: 'https://rep.fortivaretailcredit.com/',
            target: 'blank',
          })
        }

        //View More Btn
        if (this.linkable(a)) {
          list.push({
            key: 'linkable',
            type: 'action',
            label: 'View Loan Terms',
            action: () => {
              this.currentApplicationId = id
              this.pdfDialog = true
            },
          })
        }

        if (this.canViewMore(a)) {
          list.push({
            key: 'next',
            type: 'action',
            label: a.lender === 'wellsfargo' && a.application_status === 'error' ? 'View Error' : 'See More',
            action: () => this.goToApplication(id),
          })
        }

        if(this.approvalWellsFargo(a)) {
          list.push({
            key: 'easylink',
            type: 'action',
            label: 'Request Authorization',
            color: 'red',
            action: () => {
              this.sendAuthorizationLink(a)
            }
          })
        }

        return list
      },
      customerName (a) {
        return (a.applicant_first_name && a.applicant_last_name)
          ? a.applicant_first_name + ' ' + a.applicant_last_name
          : ''
      },
      //TODO review lender logic
      displayDate(a) {
        return [
          this.$options.filters.date(a.application_date),
          this.$options.filters.time(a.application_date)
        ]
      },
      displayStatus(a) {
        const status = a.application_status
        switch (status) {
          case 'available':
            return 'Eligible'
          default:
            return status
        }
      },
      getDetails(a) {
        const details = {
          id: a.application_id || 'N/A',
          account: a.lender_application_account_id || null,
          approvedAmount: (a.approved_loan_amount)
            ? this.$options.filters.currency(a.approved_loan_amount)
            : null,
          customerTier: a.color,
          statusMessage: a.message || null
        }

        const val = key => details[key]

        return this.detailKeys
          .filter(d => val(d.key))
          .map(d => {
            const v = val(d.key)
            return {
              ...d,
              val: val(d.key),
            }
          })
      },
      getLender(a) {
        return (a.lender !== undefined)
          ? a.lender
          : (a.lender_application_account_id === null)
            ? 'greensky'
            : 'fortiva'
      },
      getSubtext(a) {
        const subtext = []

        if (a.lender === 'greensky') {
          const plan = this.greenskyPlans.find(p => p.planNumber === a.plan_number)

          if (plan) {
            subtext.push(`Plan ${plan.planNumber}: ${plan.planLength} Months`)
            subtext.push(plan.interestRateMessage)
          }
        } else if (a.lender === 'fortiva') {
          subtext.push(`${a.term} months`)
          subtext.push(`${a.apr}% APR`)
        }

        return subtext
      },
      async goToApplication (id) {
        const application = this.applications
          .find(a => a.application_id === id)

        if (this.canViewMore(application)) {
          await this.saveApplicationToState(application)
          if (application.lender === 'wellsfargo') {
            this.$router.push({
              path: `/credit-application/wellsfargo-results/${application.c4c_application_id}?A=`
            })
          } else {
            switch (application.application_status) {
              case 'offer_available':
                this.statusPageReroute()
                this.getOffers()
                break
              case 'declined':
                this.statusPageReroute()
                break
              case 'rejected':
                this.statusPageReroute()
                break
              case 'pending':
                this.cycleGetStatus()
                this.statusPageReroute()
                break
              case 'under_review':
                this.statusPageReroute()
                break
            }
          }
          
        }
        
      },
      latestActivity(a) {
        let activity = ''

        //Check for customer name
        const name = this.customerName(a)
        if (name) activity += `<p><strong>${name}'s</strong> `
        else activity += '<p>The '

        //Add the rest
        const messageVerbiage = (a.lender === 'wellsfargo' && a.application_status === 'error') ? 'has an ' : 'is '

        activity += `request for
            ${ this.$options.filters.currency(a.requested_amount) }
            ${messageVerbiage} <span class="${this.statusClass(a)}">
              <b>${this.$options.filters.title(this.displayStatus(a))}</b></span>
          </p>`

        return activity
      },
      authorized(a) {
        return (a.lender === 'greensky' || a.lender === 'fortiva' ) && a.application_status === 'offer_accepted'
      },
      approvalWellsFargo(a) {
        return (a.lender === 'wellsfargo' ) && a.application_status === 'approved'
      },
      linkable(a) {
        return a.lender === 'greensky' && a.application_status === 'offer_accepted'
      },
      statusClass(a) {
        const v = a.application_status
        return v === 'declined'
          ? 'red--text'
          : v === 'offer_Available'
          ? 'green--text'
          : null
      },
      paginateHistory(newPage) {
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })

        this.checkStatuses()
      },
      pdfLink() {
        return (envVars.environment === 'production') 
        ? `https://projects.greensky.com/loanagreement?loanId=${this.currentApplicationId}`
        : `https://projects.uat.greensky.com/loanagreement?loanId=${this.currentApplicationId}`
      },
      saveApplicationToState (application) {
        let lender
        switch (application.lender) {
          case 'greensky':
            lender = { ...greensky }
            break
          case 'fortiva':
            lender = { ...fortiva }
            break
          case 'ygrene':
            lender = { ...ygrene }
            break
          case 'customfin':
            lender = { ...customfin }
            break
          case 'stressfree':
            lender = {...stressfree}
            break  
          case 'wellsfargo':
            lender = {...wellsfargo}
            break  
        }
        const applicationData = {
          lender,
          emailAddress: application.applicant_email,
          firstName: application.applicant_first_name,
          lastName: application.applicant_last_name,
          applicationId: application.application_id,
          applicationStatus: application.application_status,
          requestedAmount: this.$options.filters.currency(application.requested_amount)
        }
        this.setApplicationFromHistory(applicationData)
      },
      async close() {
        this.sentEmailDialog = false
        await delayMilliseconds(500)
        this.authorizationLinkLoading = true
        this.resendLink = null
        this.authorizationLinkSent = null
      },
      async resendAuthorizationLink() {
        this.resendLink = null;
        this.authorizationLinkLoading = true
        try {
          await this.resendEasyLink({ guid: this.guid})
          this.authorizationLinkLoading = false
          this.authorizationLinkSent = true
        } catch (error) {
          console.log(error)
        }
        
      },
      sendAuthorizationLink(a) {
        this.email = a.applicant_email
        this.$store.commit('setState', [`loanApplication.lender.id`, a.lender])
        try {
          this.sentEmailDialog = true
          this.sendEasyLink({
            email: this.email,
            lenderCode: a.lender,
            c4cApplicationId: a.c4c_application_id, 
            approvedLoanAmount: `$${numberWithCommas(a.approved_loan_amount)}`
          })
          .then(res => {
            if (res.data) {
              if (res.data.err) {
                if (res.data.err.code === 'UsernameExistsException') {
                  this.guid = res.data.guid
                  this.resendLink = true
                } else {
                  this.authorizationLinkLoading = false
                  return this.authorizationLinkMessaging = `<h3>${res.data.err.message}</h3>`
                }
              } else if (res.data.message) {
                this.authorizationLinkSent = true
              }
            }
            this.authorizationLinkLoading = false
          })
        } catch(error) {
          console.log(error)
        }
      },
      statusPageReroute () {
        this.$router.push({
          path: 'credit-application/status'
        })
      },
    },
  }
</script>
